import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { BarChart3, Zap, Clock } from 'lucide-react';
import { getAIUsage } from '../../services/api';

const UsageCounter = forwardRef(({ onUpdate }, ref) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = async () => {
    try {
      const response = await getAIUsage();
      setUsage(response.data.data);
      if (onUpdate) {
        onUpdate(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  // Expose fetchUsage to parent component
  useImperativeHandle(ref, () => ({
    fetchUsage
  }));

  const getTimeUntilReset = () => {
    if (!usage?.resetTime) return '';

    const now = new Date();
    const reset = new Date(usage.resetTime);
    const diff = reset - now;

    // Fix negative timer - if reset time is in the past, show "soon"
    if (diff < 0) {
      return 'soon';
    }

    const totalMinutes = Math.floor(diff / (1000 * 60));

    // If less than 60 minutes, show "X min"
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }

    // If 60 minutes or more, show "Xh Ym"
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const tier = usage?.tier || 'free';
  const isPremium = tier === 'premium';
  const isVIP = tier === 'vip';
  const isFree = tier === 'free';
  const remaining = usage?.remaining;
  const used = usage?.used || 0;
  const monthlyLimit = usage?.monthlyLimit;

  // Fix NaN% - ensure we have valid numbers
  let percentage;
  if (isPremium) {
    percentage = 0; // Premium has unlimited, show 0% (or don't show bar)
  } else if (!monthlyLimit || monthlyLimit === 0) {
    percentage = 100; // If no limit defined, show 100%
  } else {
    percentage = Math.min(100, (used / monthlyLimit) * 100);
  }

  // Debug logging
  console.log('Usage calculation:', { tier, used, monthlyLimit, percentage, remaining });

  return (
    <div className="card dark:!bg-black dark:border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Plan usage limits</h3>
        </div>
        {isPremium && (
          <span className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-semibold rounded-full">
            <Zap size={14} />
            PREMIUM
          </span>
        )}
        {isVIP && (
          <span className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold rounded-full">
            <Zap size={14} />
            VIP
          </span>
        )}
      </div>

      {isPremium ? (
        <div className="text-center py-4">
          <p className="text-3xl font-bold text-purple-600 mb-1">Unlimited</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">AI Code Generations</p>
          <div className="mt-4 bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
            <p className="text-sm text-purple-800">
              You have unlimited access to Learn To Trade AI-powered code generation
            </p>
          </div>
        </div>
      ) : isVIP ? (
        <>
          <div className="text-center py-2 mb-4">
            <p className="text-3xl font-bold text-blue-600 mb-1">1000</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Monthly AI Code Generations</p>
          </div>
          <div className="mb-4">
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-sm text-gray-600">
                This month
              </span>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {remaining} remaining
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-500">
                  {used} / {monthlyLimit} used
                </div>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${percentage > 80 ? 'bg-red-500' :
                  percentage > 50 ? 'bg-yellow-500' :
                    'bg-blue-500'
                  }`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
            <p className="text-xs text-blue-800">
              VIP tier includes 1000 monthly AI generations
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="text-center py-2 mb-4">
            <p className="text-3xl font-bold text-gray-600 mb-1">5</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Daily AI Code Generations</p>
          </div>
          <div className="mb-4">
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Resets in {getTimeUntilReset()}
              </span>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {remaining} remaining
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-500">
                  {used} / {monthlyLimit || 5} used
                </div>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${percentage > 80 ? 'bg-red-500' :
                  percentage > 50 ? 'bg-yellow-500' :
                    'bg-primary-500'
                  }`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>

          {remaining === 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                Daily limit reached. Upgrade to VIP for 1000 monthly generations!
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
});

UsageCounter.displayName = 'UsageCounter';

export default UsageCounter;
